import { useEffect, useState } from 'react';
import { apiFetch, useAuthStore } from '../stores/authStore';

/**
 * Shared subscription to GET /api/trader/rate-limit-status — the Phase 2 status
 * bar's account cluster: the API tier + 429 pressure for the account the caller
 * actually trades through (org pool or personal keys). One poll loop + cached
 * value shared across consumers, mirroring useHeadroom so we don't fire the
 * request once per mounted view.
 *
 * The pressure here is REACTIVE (derived from the relay's per-account 429
 * backoff) — a predictive token-spend gauge is the Phase 3 governor's job.
 */

// Adaptive cadence (Jun 27): the relay serves this from memory (no Kalshi cost),
// but every poll still occupies a slot in the browser's ~6-connection HTTP/1.1
// pool — at the old flat 1.5s that's 40 req/min/window competing with the desk
// pollers and stacking across windows. So poll FAST (1.5s, catch a 1s backoff
// blip and feel live) only while actually throttled, and SLOW (5s) when idle —
// which is almost all the time. Cuts the idle pool footprint ~3x with no loss
// of responsiveness during an actual 429 event.
const POLL_MS_IDLE = 5_000;
const POLL_MS_ACTIVE = 1_500;

export interface RateLimitStatus {
  /** Which account the caller trades through, or null when no keys resolve. */
  scope: 'org' | 'user' | null;
  tier: 'basic' | 'advanced' | 'unknown';
  /** True while the shared account bucket is in 429 backoff right now. */
  throttled: boolean;
  /** Exponential-escalation depth — how hard/repeatedly we've hit the wall. */
  rateLimitStrikes: number;
  /** ms until the current backoff window clears (0 when not throttled). */
  msUntilClear: number;
  keyPoolMode?: string;
  keyCount: number;
  upgradedKeyCount: number;
  anyQuarantined: boolean;
  /** Relay's recent OUTBOUND request rate to this account (reads + writes/s) —
   *  drives the graduated fill. NOTE: this is OUR footprint only; external load
   *  on a shared account shows up as `throttled`, not here. */
  reqPerSec?: number;
  /** Per-second request budget for the account's tier (Basic ~20/s, Advanced
   *  ~30/s at ~10 tok/req) — the graduated meter's denominator. */
  budgetReqPerSec?: number;
}

let cached: RateLimitStatus | null = null;
let pollTimer: ReturnType<typeof setTimeout> | null = null;
let polling = false;
// Bumped on logout reset — discards an in-flight fetch that started as the
// previous user so it can't repopulate `cached` (their tier/throttle) after the
// wipe. Mirrors useUserKalshiPOS; cheap insurance even though this is non-money
// metadata that self-corrects on the next poll.
let epoch = 0;
const listeners = new Set<(s: RateLimitStatus | null) => void>();

async function fetchOnce(): Promise<void> {
  const myEpoch = epoch;
  try {
    const res = await apiFetch('/api/trader/rate-limit-status');
    if (myEpoch !== epoch || !res.ok) return;
    const next = (await res.json()) as RateLimitStatus;
    if (myEpoch !== epoch) return;
    cached = next;
    listeners.forEach((l) => l(cached));
  } catch {
    // silent — keep last-good so a blip doesn't flicker the bar
  }
}

// Recursive setTimeout (not setInterval) so each tick picks the delay from the
// CURRENT throttled state — fast while throttled, slow when idle.
function scheduleNext(): void {
  const delay = cached?.throttled ? POLL_MS_ACTIVE : POLL_MS_IDLE;
  pollTimer = setTimeout(() => {
    void fetchOnce().finally(() => {
      if (polling) scheduleNext();
    });
  }, delay);
}

function ensurePolling(): void {
  if (polling) return;
  polling = true;
  scheduleNext();
}

function stopPolling(): void {
  polling = false;
  if (pollTimer) clearTimeout(pollTimer);
  pollTimer = null;
}

/** Force a fresh fetch (e.g. right after a manual tier upgrade). */
export function bumpRateLimitStatus(): void {
  fetchOnce();
}

/** Wipe the cache — used on logout so a different user doesn't see stale. */
export function resetRateLimitStatusCache(): void {
  epoch += 1; // invalidate any in-flight fetch from the previous user.
  cached = null;
  stopPolling();
  listeners.clear();
}

export function useRateLimitStatus(): RateLimitStatus | null {
  const isAuthed = useAuthStore((s) => s.status === 'authenticated');
  const [snap, setSnap] = useState<RateLimitStatus | null>(cached);

  useEffect(() => {
    if (!isAuthed) return;
    listeners.add(setSnap);
    if (listeners.size === 1) {
      ensurePolling();
      fetchOnce();
    } else if (cached) {
      setSnap(cached);
    }
    return () => {
      listeners.delete(setSnap);
      if (listeners.size === 0) stopPolling();
    };
  }, [isAuthed]);

  return snap;
}
